import React from 'react'
import { Outlet, useNavigate, useParams, NavLink } from 'react-router-dom'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useAuth } from '@/contexts/AuthContext'
import {
  Building2,
  LayoutDashboard,
  Hospital,
  Users,
  Settings,
  ArrowLeft,
  LogOut,
  ScrollText,
  CreditCard,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { IntelliHealthChatDrawer } from '@/components/intellihealth/chat'

const OrgLayout: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>()
  const { currentOrg, orgRole, orgClinics, isLoading, exitOrgView, selectOrg } = useOrganization()
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  // Select org on mount if not already selected
  React.useEffect(() => {
    if (orgId && (!currentOrg || currentOrg.id !== orgId)) {
      selectOrg(orgId)
    }
  }, [orgId, currentOrg, selectOrg])

  const handleBackToClinicSelection = () => {
    exitOrgView()
    navigate('/clinic-selection')
  }

  const handleLogout = async () => {
    exitOrgView()
    await logout()
    navigate('/login')
  }

  if (isLoading) {
    return (
      <div className="flex h-screen">
        <div className="w-64 bg-slate-900 p-4 space-y-4">
          <Skeleton className="h-8 w-full bg-slate-700" />
          <Skeleton className="h-6 w-3/4 bg-slate-700" />
          <div className="space-y-2 mt-8">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full bg-slate-700" />
            ))}
          </div>
        </div>
        <div className="flex-1 p-8">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    )
  }

  if (!currentOrg) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-700">Organization not found</h2>
          <Button variant="link" onClick={handleBackToClinicSelection}>
            Back to clinic selection
          </Button>
        </div>
      </div>
    )
  }

  const navItems = [
    { to: `/org/${orgId}/dashboard`, icon: LayoutDashboard, label: 'Dashboard' },
    { to: `/org/${orgId}/clinics`, icon: Hospital, label: `Clinics (${orgClinics.length})` },
    { to: `/org/${orgId}/members`, icon: Users, label: 'Members' },
    { to: `/org/${orgId}/settings`, icon: Settings, label: 'Settings' },
  ]

  if (orgRole === 'org_owner' || orgRole === 'org_admin') {
    navItems.push({ to: `/org/${orgId}/billing`, icon: CreditCard, label: 'Billing' })
    navItems.push({ to: `/org/${orgId}/audit-log`, icon: ScrollText, label: 'Audit Log' })
  }

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col">
        {/* Org branding */}
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            {currentOrg.logo_url ? (
              <img src={currentOrg.logo_url} alt={currentOrg.name} className="w-8 h-8 rounded" />
            ) : (
              <div className="w-8 h-8 rounded bg-teal-600 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-sm truncate">{currentOrg.name}</h2>
              <span className="text-xs text-slate-400 capitalize">{orgRole?.replace('org_', '')}</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-teal-600 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-slate-700 space-y-2">
          <button
            onClick={handleBackToClinicSelection}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Clinics
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-400 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
          <div className="px-3 py-1">
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>

      <IntelliHealthChatDrawer />
    </div>
  )
}

export default OrgLayout
